import {
  Box,
  Button,
  Checkbox,
  CheckboxGroup,
  FormControl,
  FormErrorMessage,
  FormLabel,
  Grid,
  Heading,
  Select,
  VStack
} from '@chakra-ui/react';
import React, { useState } from 'react';
import { useDispatch } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import useAuthRole from '@hooks/useAuthRole';
import ContentLayout from '@layouts/ContentLayout';
import { createCampaignV2 } from '@store/actions/referralActions';
import InputComponent from '@components/Input';
import { COUNTRY_ISO_CODES } from '@constants/v2/common';
import { ROLES } from '@constants';

const CHANNEL_OPTIONS = [
  'Influencer',
  'Event',
  'Offline',
  'Community',
  'Digital',
  'Others'
];

const CreateCampaign = () => {
  const dispatch = useDispatch();
  const { hasAuthority } = useAuthRole();
  const navigate = useNavigate();
  const hasReferralManagerRole = hasAuthority(ROLES.REFERRAL_MANAGER);

  const [formData, setFormData] = useState({
    channel: '',
    customChannel: '',
    code: '',
    eligibleCountries: []
  });
  const countries = Object.keys(COUNTRY_ISO_CODES);

  const [formErrorMessage, setFormErrorMessage] = useState('');
  const [isInValid, setIsInValid] = useState(false);
  const [isLoading, setIsLoading] = useState(false);

  // Redirect if user doesn't have REFERRAL_MANAGER role
  if (!hasReferralManagerRole) {
    navigate('/unauthorized');
    return null;
  }

  const handleChange = (e) => {
    setIsInValid(false);
    setFormErrorMessage('');
    setFormData((prev) => ({
      ...prev,
      [e.target.name]: e.target.value
    }));
  };

  const handleCountryChange = (selected) => {
    setFormData((prev) => ({ ...prev, eligibleCountries: selected }));
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    const validateObj = validate(formData);
    if (!validateObj.isValid) {
      setIsInValid(true);
      setFormErrorMessage(validateObj.message);
      return;
    }
    createReferral(formData);
  };

  const validate = (formData) => {
    if (!formData.channel) {
      return {
        isValid: false,
        message: 'Please select a channel'
      };
    }

    if (formData.channel === 'Others' && !formData.customChannel) {
      return {
        isValid: false,
        message: 'Please enter a custom channel name'
      };
    }

    if (!formData.code) {
      return {
        isValid: false,
        message: 'Please enter a campaign code'
      };
    }

    if (!formData.eligibleCountries.length) {
      return {
        isValid: false,
        message: 'Please select at least one eligible country'
      };
    }

    return {
      isValid: true
    };
  };

  const createReferral = (formData) => {
    setIsLoading(true);

    const campaignData = {
      channel: formData.channel === 'Others' ? formData.customChannel : formData.channel,
      code: formData.code,
      eligibleCountries: formData.eligibleCountries
    };

    const onSuccess = () => {
      setIsLoading(false);
      navigate('/campaign?refresh=true', {
        replace: true
      });
    };

    const onFinish = () => setIsLoading(false);

    dispatch(createCampaignV2(campaignData, onSuccess, null, onFinish));
  };

  return (
    <ContentLayout>
      <VStack alignItems={'flex-start'} p={10} gap={4}>
        <Heading color={'colorPrimary'}>Create Campaign</Heading>
        <FormControl color={'white'} maxW={'50rem'} isInvalid={isInValid}>
          <VStack
            gap={4}
            alignItems={'stretch'}
            w={'100%'}
            as={'form'}
            onSubmit={handleSubmit}
          >
            {/* CHANNEL DROPDOWN */}
            <Grid
              gridTemplateColumns={{
                base: '1fr',
                md: '0.4fr 0.6fr'
              }}
            >
              <FormLabel display={'flex'} alignItems={'center'} mb={0}>
                Channel:
              </FormLabel>
              <Box position="relative" width={'100%'} maxW="30rem">
                <Select
                  position={'relative'}
                  placeholder="Select channel"
                  bg={'black'}
                  onChange={handleChange}
                  value={formData.channel}
                  name={'channel'}
                  boxShadow={'none !important'}
                  required
                  size="lg"
                  color="white"
                  rounded={'lg'}
                  focusBorderColor="brand.400"
                  autoFocus={false}
                  border={'none'}
                  errorBorderColor="red.300"
                  style={{
                    border: 0,
                    background: '#414141',
                    gap: '12px',
                    width: '100%',
                    maxWidth: '30rem',
                    margin: '0',
                    borderRadius: '12px'
                  }}
                >
                  {CHANNEL_OPTIONS.map((channel) => (
                    <option value={channel} key={channel}>
                      {channel}
                    </option>
                  ))}
                </Select>
              </Box>
            </Grid>

            {/* CUSTOM CHANNEL INPUT - Only shown when "Others" is selected */}
            {formData.channel === 'Others' && (
              <Grid
                gridTemplateColumns={{
                  base: '1fr',
                  md: '0.4fr 0.6fr'
                }}
              >
                <FormLabel display={'flex'} alignItems={'center'} mb={0}>
                  Custom Channel:
                </FormLabel>
                <InputComponent
                  type="text"
                  handleChange={handleChange}
                  value={formData.customChannel}
                  name={'customChannel'}
                  required
                  errorBorderColor="#ffffff"
                  boxShadow={'none !important'}
                  width="100%"
                  maxWidth="30rem"
                  placeholder="Enter channel name"
                />
              </Grid>
            )}

            {/* CAMPAIGN CODE */}
            <Grid
              gridTemplateColumns={{
                base: '1fr',
                md: '0.4fr 0.6fr'
              }}
            >
              <FormLabel display={'flex'} alignItems={'center'} mb={0}>
                Campaign Code:
              </FormLabel>
              <InputComponent
                type="text"
                handleChange={handleChange}
                value={formData.code}
                name={'code'}
                required
                errorBorderColor="#ffffff"
                boxShadow={'none !important'}
                width="100%"
                maxWidth="30rem"
              />
            </Grid>

            {/* ELIGIBLE COUNTRIES */}
            <Grid
              gridTemplateColumns={{
                base: '1fr',
                md: '0.4fr 0.6fr'
              }}
              alignItems="center"
            >
              <FormLabel display="flex" alignItems="center" mb={0}>
                Eligible Countries
              </FormLabel>
              <CheckboxGroup
                value={formData.eligibleCountries}
                onChange={handleCountryChange}
              >
                <Grid
                  gridTemplateColumns={{
                    base: '1fr',
                    sm: 'repeat(2, 1fr)',
                    md: 'repeat(3, 1fr)'
                  }}
                  gap={2}
                  maxHeight="200px"
                  overflowY="auto"
                  border="1px solid #ccc"
                  borderRadius="md"
                  p={2}
                >
                  {countries.map((country) => (
                    <Checkbox key={country} value={country}>
                      {country}
                    </Checkbox>
                  ))}
                </Grid>
              </CheckboxGroup>
            </Grid>

            <FormErrorMessage>{formErrorMessage}</FormErrorMessage>
            <Button
              colorScheme="brand"
              color="altGray.400"
              mr={3}
              size={'md'}
              type="submit"
              alignSelf={'center'}
              isLoading={isLoading}
            >
              Submit Now
            </Button>
          </VStack>
        </FormControl>
      </VStack>
    </ContentLayout>
  );
};

export default CreateCampaign;
